import { useState } from "react";

import { GitBranch, Archive, ArchiveRestore, Eye, EyeOff, GitMerge } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { BranchMetadata } from "./BranchMetadata";

interface BranchInfo {
  id: string;
  name: string;
  branch_name?: string | null;
  is_branch?: boolean;
  parent_session_id?: string | null;
  new_messages?: number;
  memories_created?: number;
  last_activity_at?: string;
  archived?: boolean;
}

interface BranchListProps {
  currentSessionId: string;
  branches: BranchInfo[];
  onSwitchBranch: (branchId: string) => void;
  onArchiveBranch: (branchId: string, reason?: string) => void;
  onUnarchiveBranch: (branchId: string) => void;
  onMergeBranch: (branch: BranchInfo) => void;
}

export function BranchList({
  currentSessionId,
  branches,
  onSwitchBranch,
  onArchiveBranch,
  onUnarchiveBranch,
  onMergeBranch
}: BranchListProps) {
  const [showArchived, setShowArchived] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [branchToArchive, setBranchToArchive] = useState<BranchInfo | null>(null);
  const [archiveReason, setArchiveReason] = useState("");

  const activeBranches = branches.filter(b => !b.archived);
  const archivedBranches = branches.filter(b => b.archived);

  const displayedBranches = showArchived 
    ? branches 
    : activeBranches;

  const handleArchiveClick = (branch: BranchInfo) => {
    setBranchToArchive(branch);
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = () => {
    if (branchToArchive) {
      onArchiveBranch(branchToArchive.id, archiveReason.trim() || undefined);
      setArchiveDialogOpen(false);
      setBranchToArchive(null);
      setArchiveReason("");
    }
  };

  if (branches.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <GitBranch className="h-4 w-4" />
          Branch History
          {archivedBranches.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {archivedBranches.length} archived
            </Badge>
          )}
        </h3>
        {archivedBranches.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
            className="text-xs"
          >
            {showArchived ? (
              <>
                <EyeOff className="h-3 w-3 mr-1" />
                Hide Archived
              </>
            ) : (
              <>
                <Eye className="h-3 w-3 mr-1" />
                Show Archived
              </>
            )}
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {displayedBranches.map((branch) => {
          const isCurrent = branch.id === currentSessionId;
          const displayName = branch.branch_name || branch.name;

          return (
            <Card
              key={branch.id}
              className={`p-3 transition-all ${
                isCurrent
                  ? "border-primary bg-primary/5"
                  : "hover:border-border cursor-pointer"
              }`}
              onClick={() => !isCurrent && onSwitchBranch(branch.id)}
            >
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-sm truncate">
                        {displayName}
                      </h4>
                      {isCurrent && (
                        <Badge variant="default" className="text-xs">
                          Current
                        </Badge>
                      )}
                    </div>
                    {branch.is_branch && !branch.branch_name && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {branch.name}
                      </p>
                    )}
                  </div>
                  {!isCurrent && (
                    <div className="flex gap-1">
                      {branch.is_branch && !branch.archived && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMergeBranch(branch);
                          }}
                          className="h-7 px-2"
                          title="Merge this branch"
                        >
                          <GitMerge className="h-3 w-3" />
                        </Button>
                      )}
                      {branch.archived ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUnarchiveBranch(branch.id);
                          }}
                          className="h-7 px-2"
                        >
                          <ArchiveRestore className="h-3 w-3" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveClick(branch);
                          }}
                          className="h-7 px-2"
                        >
                          <Archive className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {(branch.new_messages !== undefined || branch.memories_created !== undefined) && (
                  <BranchMetadata
                    newMessages={branch.new_messages || 0}
                    memoriesCreated={branch.memories_created || 0}
                    lastActivityAt={branch.last_activity_at || new Date().toISOString()}
                    archived={branch.archived}
                    compact
                  />
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Branch</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive "{branchToArchive?.branch_name || branchToArchive?.name}". 
              You can restore it later from the archived branches view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="archive-reason" className="text-sm">
              Reason (optional)
            </Label>
            <Input
              id="archive-reason"
              placeholder="e.g., Exploration complete, decided on different approach"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setBranchToArchive(null);
              setArchiveReason("");
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveConfirm}>
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
